import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { LOADING_STATES } from '../../constants';
import { notificationService, setNotificationAuthToken } from './notificationsAPI';
import { Notification } from '../../types/sharedThesaurus';

// État initial
export interface NotificationsState {
    notifications: Notification[];
    unreadCount: number;
    pagination: {
        page: number;
        totalPages: number;
        total: number;
    };
    status: string;
    error: string | null;
    lastFetchedAt: string | null;
}

const initialState: NotificationsState = {
    notifications: [],
    unreadCount: 0,
    pagination: {
        page: 1,
        totalPages: 1,
        total: 0
    },
    status: LOADING_STATES.IDLE,
    error: null,
    lastFetchedAt: null
};

// =====================
// Async Thunks
// =====================

export const fetchNotifications = createAsyncThunk(
    'notifications/fetchAll',
    async ({ instance, options }: { 
        instance: any;
        options?: {
            page?: number;
            limit?: number;
            isRead?: boolean;
        };
    }) => {
        const accessToken = await getTokenRedirect(tokenRequest, instance);
        if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
        
        setNotificationAuthToken(accessToken);
        return await notificationService.getAll(options);
    }
);

export const fetchUnreadCount = createAsyncThunk(
    'notifications/fetchUnreadCount',
    async (instance: any) => {
        const accessToken = await getTokenRedirect(tokenRequest, instance);
        if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
        
        setNotificationAuthToken(accessToken);
        return await notificationService.getUnreadCount();
    }
);

export const markNotificationAsRead = createAsyncThunk(
    'notifications/markAsRead',
    async ({ notificationId, instance }: { notificationId: string; instance: any }) => {
        const accessToken = await getTokenRedirect(tokenRequest, instance);
        if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
        
        setNotificationAuthToken(accessToken);
        return await notificationService.markAsRead(notificationId);
    }
);

export const markAllNotificationsAsRead = createAsyncThunk(
    'notifications/markAllAsRead',
    async (instance: any) => {
        const accessToken = await getTokenRedirect(tokenRequest, instance);
        if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
        
        setNotificationAuthToken(accessToken);
        return await notificationService.markAllAsRead();
    }
);

export const deleteNotification = createAsyncThunk(
    'notifications/delete',
    async ({ notificationId, instance }: { notificationId: string; instance: any }) => {
        const accessToken = await getTokenRedirect(tokenRequest, instance);
        if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
        
        setNotificationAuthToken(accessToken);
        await notificationService.delete(notificationId);
        return notificationId;
    }
);

// =====================
// Slice
// =====================

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        clearNotificationError(state) {
            state.error = null;
        },
        resetNotificationsState(state) {
            return initialState;
        },
        // Pour les mises à jour en temps réel (WebSocket futur)
        addNotification(state, action: PayloadAction<Notification>) {
            state.notifications.unshift(action.payload);
            if (!action.payload.isRead) {
                state.unreadCount += 1;
            }
            state.pagination.total += 1;
        },
        // Décrémenter le compteur localement
        decrementUnreadCount(state) {
            if (state.unreadCount > 0) {
                state.unreadCount -= 1;
            }
        }
    },
    extraReducers: (builder) => {
        // Fetch all notifications
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.notifications = action.payload.data;
                state.pagination = action.payload.pagination;
                state.lastFetchedAt = new Date().toISOString();
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.status = LOADING_STATES.FAILED;
                state.error = action.error.message || 'Erreur lors du chargement des notifications';
            });

        // Fetch unread count
        builder
            .addCase(fetchUnreadCount.fulfilled, (state, action) => {
                state.unreadCount = action.payload;
            });

        // Mark as read
        builder
            .addCase(markNotificationAsRead.fulfilled, (state, action) => {
                const index = state.notifications.findIndex(n => n._id === action.payload._id);
                if (index !== -1) {
                    const wasUnread = !state.notifications[index].isRead;
                    state.notifications[index] = action.payload;
                    if (wasUnread && state.unreadCount > 0) {
                        state.unreadCount -= 1;
                    }
                }
            });

        // Mark all as read
        builder
            .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
                state.notifications = state.notifications.map(n => ({
                    ...n,
                    isRead: true
                }));
                state.unreadCount = 0;
            });

        // Delete notification
        builder
            .addCase(deleteNotification.fulfilled, (state, action) => {
                const deletedNotification = state.notifications.find(n => n._id === action.payload);
                state.notifications = state.notifications.filter(n => n._id !== action.payload);
                if (deletedNotification && !deletedNotification.isRead && state.unreadCount > 0) {
                    state.unreadCount -= 1;
                }
                state.pagination.total -= 1;
            });
    }
});

export const {
    clearNotificationError,
    resetNotificationsState,
    addNotification,
    decrementUnreadCount
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
